import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import CosmicLogo from "../components/common/CosmicLogo";
import useLiveEphemeris from "../hooks/useLiveEphemeris";
import { auth, db, googleProvider } from "../firebase";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup, updateProfile } from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { planetColors, getRashiColor, getNakshatraColor, standardPlanets } from "../utils/astroUtils";
import LiveClockPanel from "../components/CosmicClock/LiveClockPanel";
import UserProfileModal from "../components/Dashboard/UserProfileModal";
import { loadRazorpayScript, createRazorpaySubscription } from "../services/paymentService";
import "./LandingPage.css";

/* ── tiny helpers ─────────────────────────────────────────────── */
const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_BASE_URL || `http://${window.location.hostname}:5001`);

/* ── Language Toggle ──────────────────────────────────────────────────── */
const LangToggle = () => {
  const { lang, toggleLang } = useLanguage();
  const isHindi = lang === 'hi';
  return (
    <button className="nav-lang-toggle" onClick={toggleLang}
      title={isHindi ? 'Switch to English' : 'हिंदी में बदलें'}>
      <span className={`nav-lang-opt ${!isHindi ? 'nav-lang-active' : ''}`}>EN</span>
      <span className="nav-lang-divider">|</span>
      <span className={`nav-lang-opt ${isHindi ? 'nav-lang-active' : ''}`}>हि</span>
    </button>
  );
};

/* ── Top Navbar ───────────────────────────────────────────────── */
export const LandingNavbar = ({ onSignIn, onSignUp, currentUser, onProfileClick }) => {
  // const navigate = useNavigate();
  const { t } = useLanguage();
  return (
    <nav className="lp-nav" style={{ padding: '0.5rem 1.5rem', height: '64px', boxSizing: 'border-box', fontFamily: 'Inter, sans-serif' }}>
      <Link to="/" className="lp-nav-brand" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}>
        <CosmicLogo size={48} style={{ marginRight: '14px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '4px' }}>
          <h1 className="text-gradient-gold nav-brand-title" style={{ fontFamily: 'Antonio, sans-serif', fontSize: '1.8rem', fontWeight: '600', letterSpacing: '6px', margin: 0, lineHeight: '1', textTransform: 'uppercase' }}>COSPYRIA</h1>
          <span className="nav-brand-subtitle" style={{ fontSize: '0.6rem', color: '#fff', letterSpacing: '2px', fontWeight: '300', marginTop: '4px' }}>VEDIC ASTROLOGY ENGINE</span>
        </div>
      </Link>
      <div className="nav-right" style={{ flex: 'unset', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <LangToggle />
        {currentUser ? (
          <button className="nav-profile-btn" onClick={onProfileClick}>
            <span className="icon">👤</span> {t('Profile')}
          </button>
        ) : (
          <>
            <button className="nav-action-btn" onClick={onSignIn}>{t('Sign In')}</button>
            <button className="nav-auth-btn" onClick={onSignUp}>{t('Get Started')}</button>
          </>
        )}
      </div>
    </nav>
  );
};

/* ── Auth Modal ───────────────────────────────────────────────── */
export const AuthModal = ({ mode, onClose, onAuthSuccess, onToggleMode }) => {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      let isPrem = false;
      if (mode === "signup") {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName: name });
        // Create user document in Firestore
        await setDoc(doc(db, "users", userCredential.user.uid), {
          isPremium: false,
          createdAt: serverTimestamp(),
          email: email,
          displayName: name
        }, { merge: true });

        // Send Welcome Email
        try {
          await fetch(`${API}/api/payments/welcome-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, name })
          });
        } catch (e) {
          console.error("Welcome email error:", e);
        }
        isPrem = false;
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const userSnap = await getDoc(doc(db, "users", userCredential.user.uid));
        isPrem = userSnap.exists() ? userSnap.data().isPremium : false;
      }
      localStorage.removeItem("kundliData");
      localStorage.removeItem("kundliChartData");
      onAuthSuccess(isPrem);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Ensure user document exists
      const userRef = doc(db, "users", result.user.uid);
      const userSnap = await getDoc(userRef);
      let isPrem = false;
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          isPremium: false,
          createdAt: serverTimestamp(),
          email: result.user.email,
          displayName: result.user.displayName
        }, { merge: true });

        // Send Welcome Email
        try {
          await fetch(`${API}/api/payments/welcome-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: result.user.email, name: result.user.displayName })
          });
        } catch (e) {
          console.error("Welcome email error:", e);
        }
      } else {
        isPrem = userSnap.data().isPremium;
      }
      localStorage.removeItem("kundliData");
      localStorage.removeItem("kundliChartData");
      onAuthSuccess(isPrem);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="lp-modal" style={{ maxWidth: '400px' }}>
        <button className="lp-modal-close" onClick={onClose}>✕</button>
        <div className="lp-modal-header">
          <span className="lp-modal-icon">🪐</span>
          <h2>{mode === "signup" ? t("Create Your Cospyria Profile") : t("Welcome Back")}</h2>
          <p>{mode === "signup" ? t("Sign up to save your charts") : t("Log in to access your dashboard")}</p>
        </div>

        <form className="kf-form kf-compact" onSubmit={handleSubmit}>
          <div className="kf-grid" style={{ gridTemplateColumns: '1fr', gap: '1rem' }}>
            {mode === "signup" && (
              <div className="kf-field">
                <label className="kf-label">{t('Name')}</label>
                <input
                  className="kf-input"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('Enter your name')}
                />
              </div>
            )}
            <div className="kf-field">
              <label className="kf-label">{t('Email')}</label>
              <input
                className="kf-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('Enter your email')}
              />
            </div>
            <div className="kf-field">
              <label className="kf-label">{t('Password')}</label>
              <input
                className="kf-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('Enter your password')}
              />
            </div>
          </div>
          {error && <div className="kf-err" style={{ marginTop: '0.5rem', textAlign: 'center' }}>{error}</div>}
          <button type="submit" className="kf-submit" style={{ marginTop: '1.5rem', width: '100%' }} disabled={loading}>
            <span>✦</span> {loading ? t("Authenticating...") : (mode === "signup" ? t("Sign Up") : t("Log In"))}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', margin: '1.5rem 0', color: '#8b87a8' }}>
            <hr style={{ flex: 1, borderColor: 'rgba(212,175,55,0.2)' }} />
            <span style={{ padding: '0 10px', fontSize: '0.85rem' }}>OR</span>
            <hr style={{ flex: 1, borderColor: 'rgba(212,175,55,0.2)' }} />
          </div>

          <button
            type="button"
            className="lp-btn-outline"
            style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            {t('Continue with Google')}
          </button>

          <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.85rem', color: '#8b87a8' }}>
            {mode === "signup" ? t("Already have an account? ") : t("Don't have an account? ")}
            <span
              style={{ color: '#D4AF37', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={onToggleMode}
            >
              {mode === "signup" ? t("Log In") : t("Sign Up")}
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Hero Section ─────────────────────────────────────────────── */
const HeroSection = ({ onGetStarted }) => {
  const { t, tp } = useLanguage();
  const { data } = useLiveEphemeris();
  const planets = data?.planets || [];
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const particlesRef = useRef([]);
  const animRef = useRef(null);

  // Stardust effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const PARTICLE_COUNT = 80;

    // Init particles
    particlesRef.current = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      baseX: 0,
      baseY: 0,
      baseSize: Math.random() * 2 + 0.3,
      opacity: Math.random() * 0.5 + 0.1,
      speed: Math.random() * 0.3 + 0.05,
      dx: (Math.random() - 0.5) * 0.3,
      dy: (Math.random() - 0.5) * 0.3,
      color: Math.random() > 0.7 ? '#D4AF37' : '#ffffff',
    }));
    particlesRef.current.forEach(p => { p.baseX = p.x; p.baseY = p.y; });

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      
      const isMobile = w < 768;
      const INFLUENCE_RADIUS = isMobile ? 60 : 120;

      particlesRef.current.forEach((p, idx) => {
        // Hide half the particles on mobile
        if (isMobile && idx % 2 !== 0) return;

        // Drift naturally
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;

        // Attract toward mouse within radius
        const dist = Math.hypot(p.x - mx, p.y - my);
        if (dist < INFLUENCE_RADIUS) {
          const force = (1 - dist / INFLUENCE_RADIUS) * 0.8;
          p.x += (mx - p.x) * force * 0.04;
          p.y += (my - p.y) * force * 0.04;
          // Glow near mouse
          const glow = 1 + force * 2;
          ctx.shadowBlur = (isMobile ? 4 : 8) * glow;
          ctx.shadowColor = p.color;
          ctx.globalAlpha = Math.min(1, p.opacity + force * 0.6);
        } else {
          ctx.shadowBlur = 0;
          ctx.globalAlpha = p.opacity;
        }

        ctx.beginPath();
        const currentSize = p.baseSize * (isMobile ? 0.6 : 1);
        ctx.arc(p.x, p.y, currentSize, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
      });

      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    const onResize = () => {
      const dpr = window.devicePixelRatio || 1;
      w = canvas.offsetWidth; h = canvas.offsetHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const handlePointerMove = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const handlePointerLeave = useCallback(() => {
    mouseRef.current = { x: -9999, y: -9999 };
  }, []);

  return (
    <section 
      className="lp-hero" 
      onPointerMove={handlePointerMove} 
      onPointerLeave={handlePointerLeave} 
      onPointerUp={handlePointerLeave} 
      onPointerCancel={handlePointerLeave}
    >
      {/* Stardust Canvas — behind everything */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          pointerEvents: 'none', zIndex: 0,
        }}
      />
      <div className="lp-hero-content" style={{ position: 'relative', zIndex: 1 }}>
        <div className="lp-hero-badge">🌠 {t('Live Celestial Tracking')}</div>
        <h1 className="lp-hero-title">
          {t('Your Life, Mapped')}<br />
          <span className="lp-gradient-text">{t('in the Stars')}</span>
        </h1>
        <p className="lp-hero-sub">
          {t('Vedic astrology powered by real-time ephemeris data. Discover your birth chart, live planetary positions, and cosmic timing — all in one place.')}
        </p>
        <div className="lp-hero-cta">
          <button className="lp-btn-primary lp-btn-lg" onClick={onGetStarted}>
            ✨ {t('Generate Your Chart')}
          </button>
          <a href="#panchang" className="lp-btn-ghost">{t("View Today's Panchang")} ↓</a>
        </div>
        <div className="lp-hero-stats">
          {planets.filter(p => standardPlanets.includes(p.name)).map(p => (
            <div className="lp-stat" key={p.name}>
              <span className="lp-stat-val" style={{ fontSize: '1.1rem', color: planetColors[p.name] || 'var(--gold)' }}>{tp(p.name)}</span>
              <span className="lp-stat-key" style={{ fontSize: '0.7rem', color: 'var(--text-dim)', letterSpacing: '0.5px' }}>{t(p.rashi)}</span>
              {p.nakshatra && <span className="lp-stat-key" style={{ fontSize: '0.62rem', color: 'rgba(180,180,220,0.7)', letterSpacing: '0.3px' }}>{t(p.nakshatra)}{p.pada ? ` · ${t('P')}${p.pada}` : ''}</span>}
            </div>
          ))}
        </div>
      </div>
      <div className="lp-hero-clock" style={{ position: 'relative', zIndex: 1 }}>
        <div className="lp-hero-clock-title">
          <span className="lp-hero-clock-dot" />
          {t('Live Cosmic Clock')}
        </div>
        <LiveClockPanel />
        <p className="lp-hero-clock-hint">
          ✦ {t('Hover or click a planet hand to reveal its live transit details')}
        </p>
        <div className="lp-trust-badges">
          <span className="lp-trust-badge"><span className="tb-icon">🚀</span> {t('NASA Live Planet Position')}</span>
          <span className="lp-trust-badge"><span className="tb-icon">🔭</span> {t('Vedic Astrology Engine')}</span>
          <span className="lp-trust-badge"><span className="tb-icon">☉</span> {t('Surya Siddhant Rules')}</span>
        </div>
        <p className="lp-clock-note">
          ⓘ {t('All calculations are based on live ephemeris data. Minor variations may occur due to real-time precision.')}
        </p>
      </div>
    </section>
  );
};



/* ── Quote Section ────────────────────────── */
const QuoteSection = () => {
  const { t } = useLanguage();
  return (
    <section className="lp-quote">
      <div className="lp-quote-inner">
        <div className="lp-quote-stars">✦ ✦ ✦</div>
        <blockquote className="lp-quote-text">
          {t('"We are all living inside a cosmic clock —')}<br />
          {t('let us learn to read its hands and schedule our lives for the better."')}
        </blockquote>
        <cite className="lp-quote-cite">— {t('Ancient Vedic Wisdom, Modernised')}</cite>
      </div>
    </section>
  );
};

/* ── Panchang Section ───────────────────────────── */
const PanchangSection = () => {
  const { t } = useLanguage();
  const [panchang, setPanchang] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/ephemeris/current?include=panchang`)
      .then(r => r.json()).then(d => setPanchang(d.panchang)).catch(() => { });
  }, []);

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const formatNakshatra = (nak) => {
    if (!nak) return nak;
    if (nak.includes('-')) {
      const parts = nak.split('-');
      return `${t(parts[0].trim())} - ${parts[1].trim()}`;
    }
    return nak;
  };

  const mainItems = panchang ? [
    { label: 'Tithi', val: panchang.Tithi, icon: '🌙', desc: panchang.Paksha === 'Shukla' ? 'Bright Fortnight' : 'Dark Fortnight' },
    { label: 'Nakshatra', val: formatNakshatra(panchang.Nakshatra), icon: '✨', desc: 'Lunar mansion', color: getNakshatraColor(panchang.Nakshatra?.split('-')[0].trim()) },
    { label: 'Yoga', val: panchang.Yoga, icon: '🔮', desc: 'Auspiciousness' },
    { label: 'Karana', val: panchang.Karana, icon: '✋', desc: 'Half-Tithi' },
    { label: 'Paksha', val: panchang.Paksha, icon: panchang?.Paksha === 'Shukla' ? '🌕' : '🌑', desc: 'Lunar phase' },
  ] : [];

  return (
    <section className="lp-section" id="panchang">
      <div className="lp-section-header">
        <span className="lp-section-badge">🗓️ {t('Vedic Almanac')}</span>
        <h2 className="lp-section-title">{t("Today's Panchang")}</h2>
        <p className="lp-section-sub">{today}</p>
      </div>
      {panchang ? (
        <div className="lp-panch-layout">
          {/* Left: Sun + Moon mega cards */}
          <div className="lp-panch-luminary-col">
            <div className="lp-panch-luminary lp-panch-sun">
              <div className="lp-panch-lum-icon">🌞</div>
              <div className="lp-panch-lum-body">
                <div className="lp-panch-lum-label">{t('Sunrise')}</div>
                <div className="lp-panch-lum-time">{panchang.Sunrise || '–'}</div>
                <div className="lp-panch-lum-label" style={{ marginTop: '0.6rem' }}>{t('Sunset')}</div>
                <div className="lp-panch-lum-time">{panchang.Sunset || '–'}</div>
                <div className="lp-panch-lum-label" style={{ marginTop: '0.6rem' }}>{t('Day')}</div>
                <div className="lp-panch-lum-val">{t(panchang.Day) || '–'}</div>
              </div>
            </div>
            <div className="lp-panch-luminary lp-panch-moon">
              <div className="lp-panch-lum-icon">🌙</div>
              <div className="lp-panch-lum-body">
                <div className="lp-panch-lum-label">{t('Moonrise')}</div>
                <div className="lp-panch-lum-time">{panchang.Moonrise || '–'}</div>
                <div className="lp-panch-lum-label" style={{ marginTop: '0.6rem' }}>{t('Moonset')}</div>
                <div className="lp-panch-lum-time">{panchang.Moonset || '–'}</div>
                <div className="lp-panch-lum-label" style={{ marginTop: '0.6rem' }}>{t('Paksha')}</div>
                <div className="lp-panch-lum-val">{t(panchang.Paksha) || '–'}</div>
              </div>
            </div>
          </div>
          {/* Right: 5 core panchang elements in a styled grid */}
          <div className="lp-panch-five-grid">
            {mainItems.map(item => (
              <div className="lp-panch-five-card" key={item.label}>
                <span className="lp-panch-five-icon">{item.icon}</span>
                <div className="lp-panch-five-body">
                  <span className="lp-panch-five-label">{t(item.label)}</span>
                  <span className="lp-panch-five-val" style={item.color ? { color: item.color } : {}}>{t(item.val) || '–'}</span>
                  <span className="lp-panch-five-desc">{t(item.desc)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="lp-loading">{t('Loading panchang…')}</div>
      )}
    </section>
  );
};

const RASHIS_HO = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];
const RASHI_SYMS = ["\u2648", "\u2649", "\u264A", "\u264B", "\u264C", "\u264D", "\u264E", "\u264F", "\u2650", "\u2651", "\u2652", "\u2653"];
const RASHI_ELEMENTS = ["Fire", "Earth", "Air", "Water", "Fire", "Earth", "Air", "Water", "Fire", "Earth", "Air", "Water"];
const RASHI_LORDS = ["Mars", "Venus", "Mercury", "Moon", "Sun", "Mercury", "Venus", "Mars", "Jupiter", "Saturn", "Saturn", "Jupiter"];
const RASHI_LORD_COLORS = [
  '#FF3B3B', '#FF9ED1', '#2ECC71', '#E8F0FF', '#FF8C00', '#2ECC71',
  '#FF9ED1', '#FF3B3B', '#FFD700', '#8CA5FF', '#8CA5FF', '#FFD700',
];

const TAB_META = {
  today: { planet: "Moon", desc: "Daily emotional & intuitive guidance based on Moon’s transit" },
  weekly: { planet: "Mars", desc: "Weekly action & energy forecast based on Mars’ transit" },
  monthly: { planet: "Sun", desc: "Monthly soul-level focus based on Sun’s transit" },
};

const HoroscopeSection = ({ onGetStarted }) => {
  const { t, tp } = useLanguage();
  const [tab, setTab] = useState("today");
  const [selected, setSelected] = useState(null);
  const [hoData, setHoData] = useState(null);
  const [loadingHo, setLoadingHo] = useState(true);

  useEffect(() => {
    setLoadingHo(true);
    setSelected(null);
    const endpoint = tab === 'today' ? 'daily' : tab;
    fetch(`${API}/api/ephemeris/horoscope/${endpoint}`)
      .then(res => res.json())
      .then(data => { setHoData(data); setLoadingHo(false); })
      .catch(() => setLoadingHo(false));
  }, [tab]);

  const tabMeta = TAB_META[tab];

  return (
    <section className="lp-section lp-section-dark" id="horoscope">
      <div className="lp-section-header">
        <span className="lp-section-badge">☉ {t('Vedic Rashifal')}</span>
        <h2 className="lp-section-title">{t('Horoscope')}</h2>
        <p className="lp-section-sub">
          {t(tabMeta.desc)}
          {!loadingHo && hoData && <span className="lp-ho-planet-tag">● {tp(tabMeta.planet)} {t('in transit')}</span>}
        </p>
        <div className="lp-tabs" style={{ marginTop: '1.5rem' }}>
          {Object.entries(TAB_META).map(([tKey, m]) => (
            <button key={tKey} className={`lp-tab${tab === tKey ? ' active' : ''}`} onClick={() => setTab(tKey)}>
              {tKey === 'today' ? t('Today') : tKey === 'weekly' ? t('This Week') : t('This Month')}
            </button>
          ))}
        </div>
      </div>

      <div className="lp-ho-layout">
        {/* Zodiac grid */}
        <div className="lp-rashi-grid-v2">
          {RASHIS_HO.map((r, i) => (
            <div
              key={r}
              className={`lp-rashi-card-v2${selected === r ? ' selected' : ''}`}
              onClick={() => setSelected(selected === r ? null : r)}
              style={{ '--rashi-color': getRashiColor(r), '--lord-color': RASHI_LORD_COLORS[i] }}
            >
              <span className="lp-rc-sym" style={{ color: RASHI_LORD_COLORS[i], textShadow: `0 0 14px ${RASHI_LORD_COLORS[i]}`, filter: `drop-shadow(0 0 6px ${RASHI_LORD_COLORS[i]})` }}>{RASHI_SYMS[i]}︎</span>
              <span className="lp-rc-name">{t(r)}</span>
              <span className="lp-rc-lord" style={{ color: RASHI_LORD_COLORS[i] }}>{tp(RASHI_LORDS[i])}</span>
            </div>
          ))}
        </div>

        {/* Reading panel */}
        <div className={`lp-ho-reading-panel${selected ? ' visible' : ''}`}>
          {selected ? (
            <>
              <div className="lp-ho-rp-header">
                <span className="lp-ho-rp-sym" style={{ color: getRashiColor(selected) }}>
                  {RASHI_SYMS[RASHIS_HO.indexOf(selected)]}
                </span>
                <div>
                  <div className="lp-ho-rp-name" style={{ color: getRashiColor(selected) }}>{t(selected)}</div>
                  <div className="lp-ho-rp-meta">
                    {t(RASHI_ELEMENTS[RASHIS_HO.indexOf(selected)])} · {t('Lord')}: {tp(RASHI_LORDS[RASHIS_HO.indexOf(selected)])}
                  </div>
                </div>
              </div>
              <div className="lp-ho-rp-divider" style={{ borderColor: getRashiColor(selected) + '44' }} />
              <p className="lp-ho-rp-text">
                {loadingHo
                  ? <span className="lp-ho-loading">✨ {t('Consulting the stars…')}</span>
                  : (Array.isArray(hoData?.[selected])
                    ? hoData[selected].map(str => t(str)).join(' ')
                    : hoData?.[selected])
                  || t('No prediction available.')}
              </p>
              <button className="lp-ho-rp-cta" onClick={onGetStarted}>
                {t('Get Personalised Chart')} →
              </button>
            </>
          ) : (
            <div className="lp-ho-rp-placeholder">
              <span style={{ fontSize: '2rem' }}>&#9654;</span>
              <p>{t('Select your Rashi to read your')} {t(tab === 'today' ? 'daily' : tab)} {t('prediction')}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};


/* ── Vedic Calendar Section ───────────────────────────────────── */

const PAKSHA_SHORT = { Shukla: 'S', Krishna: 'K' };
const EVENT_COLORS = {
  Festival: '#FFD700',
  Purnima: '#E8F0FF',
  Amavasya: '#9B59B6',
  Ekadashi: '#2ECC71',
  Sankranti: '#FF8C00',
  Auspicious: '#00CED1',
};

const VedicCalendarSection = () => {
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calData, setCalData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  useEffect(() => {
    setLoading(true);
    setSelected(null);
    fetch(`${API}/api/ephemeris/calendar/month/full?year=${year}&month=${month}`)
      .then(r => r.json())
      .then(d => { setCalData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [year, month]);

  const handlePrev = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const handleNext = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const getDayObj = (day) => calData?.days?.find(d => d.day === day);

  // Tithi display helpers
  const tithiCode = (d) => d ? `${t(PAKSHA_SHORT[d.paksha] || '')}${d.tithiNum}` : '';
  const tithiCode2 = (d) => {
    if (!d?.dualTithi) return null;
    const n2 = d.tithiAtSunset !== d.tithiAtSunrise ? d.tithiAtSunset : null;
    if (!n2) return null;
    const num2 = (() => {
      const idx = ['Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami', 'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami', 'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima', 'Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami', 'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami', 'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Amavasya'].indexOf(n2);
      return idx >= 15 ? idx - 14 : idx + 1;
    })();
    const p2 = d.tithiAtSunset === 'Amavasya' || ['Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami', 'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami', 'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima'].indexOf(d.tithiAtSunset) === -1 ? 'K' : (d.paksha === 'Shukla' && d.tithiAtSunrise !== 'Purnima') ? 'S' : 'K';
    return `${t(p2)}${num2}`;
  };

  return (
    <section className="lp-section" id="calendar">
      <div className="lp-section-header">
        <span className="lp-section-badge">📆 {t('365-Day Almanac')}</span>
        <h2 className="lp-section-title">{t('Vedic Calendar')}</h2>
        <p className="lp-section-sub">{t('Live Vedic almanac — Tithi · Nakshatra · Yoga · Karana · Festivals, updated daily')}</p>
      </div>

      <div className="lp-vcal-wrap">
        {/* ── Month Nav ── */}
        <div className="lp-vcal-topbar">
          <button className="lp-vcal-nav" onClick={handlePrev}>‹</button>
          <div className="lp-vcal-monthtitle">
            <span className="lp-vcal-greg">{t(currentDate.toLocaleString('en-IN', { month: 'long' }))} {year}</span>
            {calData && (
              <span className="lp-vcal-hindu">
                {calData.adhikMaas && <span className="lp-vcal-adhik">{t('Adhik')}&nbsp;</span>}
                {t(calData.hinduMonthName?.toUpperCase())}
              </span>
            )}
          </div>
          <button className="lp-vcal-nav" onClick={handleNext}>›</button>
        </div>

        {/* ── Grid ── */}
        <div className="lp-vcal-grid">
          {WEEKDAYS.map(wd => (
            <div key={wd} className={`lp-vcal-head ${wd === 'Sun' ? 'sun-col' : ''}`}>{t(wd)}</div>
          ))}
          {Array.from({ length: firstDow }).map((_, i) => (
            <div key={`blank-${i}`} className="lp-vcal-cell blank" />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
            const d = getDayObj(day);
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isToday = dateStr === todayStr;
            const isSel = selected?.day === day;
            const isSun = (new Date(year, month - 1, day).getDay() === 0);
            const tc1 = tithiCode(d);
            const tc2 = tithiCode2(d);
            /* Tithi/event indicators */
            const isEkadashi = d?.tithiAtSunrise === 'Ekadashi' || d?.tithiAtSunset === 'Ekadashi';
            const isPurnima = d?.tithiAtSunrise === 'Purnima' || d?.tithiAtSunset === 'Purnima';
            const isAmavasya = d?.tithiAtSunrise === 'Amavasya' || d?.tithiAtSunset === 'Amavasya';
            const hasSankranti = d?.events?.some(ev => ev.name?.toLowerCase().includes('sankranti') || ev.name?.toLowerCase().includes('sankranthi') || ev.name?.toLowerCase().includes('makar') || ev.name?.toLowerCase().includes('mesa'));
            const hasFestival = d?.events?.some(ev => ev.name && !ev.name.toLowerCase().includes('sankranti') && !ev.name.toLowerCase().includes('sankranthi'));

            return (
              <div
                key={day}
                className={`lp-vcal-cell${isToday ? ' today' : ''}${isSel ? ' selected' : ''}${isSun ? ' sun-col' : ''}${d?.bhadra ? ' bhadra' : ''}${d?.panchak ? ' panchak' : ''}${d?.events?.length ? ' has-event' : ''}`}
                onClick={() => setSelected(isSel ? null : d)}
              >
                {/* Day number */}
                <div className="lp-vcal-daynum">{day}</div>

                {/* Indicator dots: Ekadashi / Purnima / Amavasya / Sankranti / Festival */}
                {(isEkadashi || isPurnima || isAmavasya || hasSankranti || hasFestival) && (
                  <div className="lp-vcal-dots">
                    {isEkadashi && <span className="lp-vcal-dot lp-dot-ekadashi" title="Ekadashi" />}
                    {isPurnima && <span className="lp-vcal-dot lp-dot-purnima" title="Purnima" />}
                    {isAmavasya && <span className="lp-vcal-dot lp-dot-amavasya" title="Amavasya" />}
                    {hasSankranti && <span className="lp-vcal-dot lp-dot-sankranti" title="Sankranti" />}
                    {hasFestival && <span className="lp-vcal-dot lp-dot-event" title="Festival" />}
                  </div>
                )}
                {d && <>
                  {/* Tithi badge(s) */}
                  <div className="lp-vcal-tithi-row">
                    <span className="lp-vcal-tithi" style={{ color: d.paksha === 'Shukla' ? '#c8b97a' : '#b0aacc' }}>
                      {tc1}{tc2 && <span className="lp-vcal-tithi-arr">→</span>}{tc2 && <span style={{ color: '#aaa' }}>{tc2}</span>}
                    </span>
                    <div className="lp-vcal-chips-inline">
                      {d.bhadra && <span className="lp-chip lp-chip-bhadra" title="Bhadra">Bh</span>}
                      {d.panchak && <span className="lp-chip lp-chip-panchak" title="Panchak">Pa</span>}
                    </div>
                  </div>

                  {/* Nakshatra */}
                  <div className="lp-vcal-nak">{t(d.nakshatra)}</div>

                  {/* Yoga */}
                  <div className="lp-vcal-yoga">{t(d.yoga)}</div>

                  {/* Events — all of them, no truncation */}
                  {d.events?.length > 0 && (
                    <div className="lp-vcal-events-col">
                      {d.events.map((ev, i) => (
                        <div
                          key={i}
                          className="lp-vcal-fest"
                          style={{ color: EVENT_COLORS[ev.type] || '#FFD700' }}
                        >{t(ev.name)}</div>
                      ))}
                    </div>
                  )}
                </>}
              </div>
            );
          })}
        </div>

        {/* ── Legend ── */}
        <div className="lp-vcal-legend">
          {Object.entries(EVENT_COLORS).map(([k, c]) => (
            <span key={k} className="lp-vcal-leg-item">
              <span className="lp-vcal-leg-dot" style={{ background: c }} />{t(k)}
            </span>
          ))}
          <span className="lp-vcal-leg-item"><span className="lp-chip lp-chip-bhadra">Bh</span>&nbsp;{t('Bhadra')}</span>
          <span className="lp-vcal-leg-item"><span className="lp-chip lp-chip-panchak">Pa</span>&nbsp;{t('Panchak')}</span>
        </div>

        {/* ── Detail Panel ── */}
        {selected && (
          <div className="lp-vcal-detail">
            <button className="lp-vcal-detail-close" onClick={() => setSelected(null)}>✕</button>

            <div className="lp-vcal-detail-date">
              {new Date(selected.date + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
            <div className="lp-vcal-detail-hindu">
              {selected.adhikMaas && <span className="lp-vcal-adhik">{t('Adhik')} </span>}
              {t(selected.hinduMonth)} — {t(selected.paksha)} {t('Paksha')}
            </div>

            {/* ── Tithi Section (single or dual) ── */}
            <div className="lp-vcal-tithi-section">
              <div className="lp-vcal-tithi-section-title">
                {t('Tithi')}{selected.dualTithi ? ` (${t('2 tithis today')})` : ''}
              </div>

              {/* Tithi 1 */}
              <div className="lp-vcal-tithi-card">
                <div className="lp-vcal-tithi-card-name">
                  <span className="lp-vcal-tithi-num">{tithiCode(selected)}</span>
                  &nbsp;{t(selected.tithiAtSunrise)}
                </div>
                <div className="lp-vcal-tithi-times">
                  <span className="lp-vcal-time-row"><span className="lp-vcal-time-lbl">{t('Begins')}</span><span className="lp-vcal-time-val">{selected.tithiStartIST || '—'}</span></span>
                  <span className="lp-vcal-time-row"><span className="lp-vcal-time-lbl">{t('Ends')}</span><span className="lp-vcal-time-val">{selected.tithiEndIST || '—'}</span></span>
                </div>
              </div>

              {/* Tithi 2 (only on dual-tithi days) */}
              {selected.dualTithi && selected.tithiAtSunset !== selected.tithiAtSunrise && (
                <div className="lp-vcal-tithi-card lp-vcal-tithi-card-2">
                  <div className="lp-vcal-tithi-card-name">
                    <span className="lp-vcal-tithi-num">{tithiCode2(selected)}</span>
                    &nbsp;{t(selected.tithiAtSunset)}
                  </div>
                  <div className="lp-vcal-tithi-times">
                    <span className="lp-vcal-time-row"><span className="lp-vcal-time-lbl">{t('Begins')}</span><span className="lp-vcal-time-val">{selected.tithiEndIST || '—'}</span></span>
                    <span className="lp-vcal-time-row"><span className="lp-vcal-time-lbl">{t('Ends')}</span><span className="lp-vcal-time-val">{selected.tithiEnd2IST || '—'}</span></span>
                  </div>
                </div>
              )}
            </div>

            {/* ── Other Panchang Details ── */}
            <div className="lp-vcal-detail-grid">
              {[
                [t('Nakshatra'), `${t(selected.nakshatra)} (${t('Pada')} ${selected.pada})`],
                [t('Yoga'), t(selected.yoga)],
                [t('Karana'), t(selected.karana)],
                [t('Bhadra'), selected.bhadra ? `⚠ ${t('Active')}` : t('None')],
                [t('Panchak'), selected.panchak ? `⚠ ${t('Active')}` : t('None')],
              ].map(([k, v]) => (
                <div key={k} className="lp-vcal-detail-row">
                  <span className="lp-vcal-detail-key">{k}</span>
                  <span className={`lp-vcal-detail-val${(k === 'Bhadra' && selected.bhadra) || (k === 'Panchak' && selected.panchak) ? ' warn' : ''}`}>{v}</span>
                </div>
              ))}
            </div>

            {/* ── Events ── */}
            {selected.events?.length > 0 && (
              <div className="lp-vcal-detail-events">
                <div className="lp-vcal-detail-evtitle">{t('Events & Festivals')}</div>
                {selected.events.map((ev, i) => (
                  <div key={i} className="lp-vcal-detail-ev" style={{ borderLeftColor: EVENT_COLORS[ev.type] || '#FFD700' }}>
                    <span className="lp-vcal-detail-evname">{t(ev.name)}</span>
                    <span className="lp-vcal-detail-evtype" style={{ color: EVENT_COLORS[ev.type] || '#FFD700' }}>{t(ev.type)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {loading && <div className="lp-vcal-loading">✨ {t('Computing panchang…')}</div>}
      </div>
    </section>
  );
};


/* ── Vedic Muhurat Section ───────────────────────────────────── */
const EVENT_KEYS = ['vivah', 'griha', 'vaahan', 'vyapar', 'bhoomi', 'vidya', 'yatra', 'mundan'];
const EVENT_META_FE = {
  vivah: { label: 'Vivah', english: 'Marriage', icon: '💒', color: '#FF6B9D' },
  griha: { label: 'Griha Pravesh', english: 'House Entry', icon: '🏠', color: '#4ECDC4' },
  vaahan: { label: 'Vaahan Puja', english: 'Vehicle Purchase', icon: '🚗', color: '#45B7D1' },
  vyapar: { label: 'Vyapar Aarambh', english: 'New Business', icon: '💼', color: '#96CEB4' },
  bhoomi: { label: 'Bhoomi Puja', english: 'Property Purchase', icon: '🏗️', color: '#FFEAA7' },
  vidya: { label: 'Vidyarambha', english: 'Start Education', icon: '📚', color: '#A29BFE' },
  yatra: { label: 'Yatra', english: 'Travel / Journey', icon: '✈️', color: '#74B9FF' },
  mundan: { label: 'Mundan', english: 'First Haircut', icon: '✂️', color: '#FD79A8' },
};
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const QUALITY_BG = {
  Excellent: 'rgba(46,204,113,0.12)',
  Auspicious: 'rgba(39,174,96,0.1)',
  Moderate: 'rgba(243,156,18,0.1)',
  Inauspicious: 'rgba(231,76,60,0.1)',
  Avoid: 'rgba(192,57,43,0.12)',
};

const VedicMuhuratSection = () => {
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [muhuratData, setMuhuratData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeEvent, setActiveEvent] = useState('vivah'); // filter
  const [expanded, setExpanded] = useState(null);    // expanded day
  const listRef = useRef(null);

  useEffect(() => {
    if (!loading && muhuratData && listRef.current) {
      // Small timeout to ensure DOM is fully rendered
      setTimeout(() => {
        const list = listRef.current;
        const todayCard = list.querySelector('.lp-muh-card.today');
        if (todayCard) {
          const scrollPos = todayCard.offsetTop - list.offsetTop - (list.clientHeight / 2) + (todayCard.clientHeight / 2);
          list.scrollTo({ top: scrollPos, behavior: 'smooth' });
        }
      }, 150);
    }
  }, [loading, muhuratData, activeEvent]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  useEffect(() => {
    setLoading(true);
    setExpanded(null);
    fetch(`${API}/api/muhurat/month?year=${year}&month=${month}`)
      .then(r => r.json())
      .then(d => { setMuhuratData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [year, month]);

  const handlePrev = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const handleNext = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Filter days to those with at least Moderate quality for selected event
  const filteredDays = (muhuratData?.days || []).map(day => {
    const m = day.muhurats.find(x => x.key === activeEvent);
    return { ...day, eventMuhurat: m };
  });

  const getMeta = (key) => EVENT_META_FE[key] || {};

  const translateReason = (str) => {
    if (str.startsWith('Auspicious nakshatra: ')) return t('Auspicious nakshatra') + ': ' + t(str.split(': ')[1]);
    if (str.startsWith('Inauspicious nakshatra: ')) return t('Inauspicious nakshatra') + ': ' + t(str.split(': ')[1]);
    if (str.startsWith('Excellent nakshatra: ')) return t('Excellent nakshatra') + ': ' + t(str.split(': ')[1]);
    if (str.startsWith('Avoid nakshatra: ')) return t('Avoid nakshatra') + ': ' + t(str.split(': ')[1]);
    if (str.startsWith('Auspicious Shukla ')) return t('Auspicious Shukla') + ' ' + t(str.replace('Auspicious Shukla ', ''));
    if (str.startsWith('Moderate Krishna ')) return t('Moderate Krishna') + ' ' + t(str.replace('Moderate Krishna ', ''));
    if (str.startsWith('Inauspicious Tithi: ')) {
      const tithi = str.split(': ')[1].split(' ')[0];
      return t('Inauspicious Tithi') + ': ' + t(tithi) + ' (' + t('Rikta') + ')';
    }
    if (str.startsWith('Dagdha Tithi (')) {
      const tithiMatch = str.match(/\((.*?) on/);
      if (tithiMatch) return t('Dagdha Tithi') + ' (' + t(tithiMatch[1]) + ' ' + t('on this weekday') + ')';
    }
    if (str.startsWith('Auspicious yoga: ')) return t('Auspicious yoga') + ': ' + t(str.split(': ')[1]);
    if (str.startsWith('Excellent yoga: ')) return t('Excellent yoga') + ': ' + t(str.split(': ')[1]);
    if (str.startsWith('Inauspicious yoga: ')) return t('Inauspicious yoga') + ': ' + t(str.split(': ')[1]);
    if (str.startsWith('Auspicious ')) return t('Auspicious') + ' ' + t(str.replace('Auspicious ', ''));
    if (str.startsWith('Inauspicious ')) return t('Inauspicious') + ' ' + t(str.replace('Inauspicious ', ''));
    return t(str);
  };

  return (
    <section className="lp-section lp-section-dark" id="muhurat">
      <div className="lp-section-header">
        <span className="lp-section-badge">🕉️ {t('Vedic Almanac')}</span>
        <h2 className="lp-section-title">{t('Vedic Muhurat')}</h2>
        <p className="lp-section-sub">{t("Auspicious timings for life's most important events — computed from Panchang")}</p>
      </div>

      <div className="lp-muh-wrap">
        {/* ── Event Filter Tabs ── */}
        <div className="lp-muh-tabs">
          {EVENT_KEYS.map(key => {
            const meta = getMeta(key);
            return (
              <button
                key={key}
                className={`lp-muh-tab${activeEvent === key ? ' active' : ''}`}
                style={activeEvent === key ? { borderColor: meta.color, color: meta.color, background: `${meta.color}18` } : {}}
                onClick={() => { setActiveEvent(key); setExpanded(null); }}
              >
                <span>{meta.icon}</span>
                <span className="lp-muh-tab-label">{t(meta.label)}</span>
              </button>
            );
          })}
        </div>

        {/* ── Month Nav ── */}
        <div className="lp-muh-topbar">
          <button className="lp-vcal-nav" onClick={handlePrev}>‹</button>
          <div className="lp-vcal-monthtitle">
            <span className="lp-vcal-greg">{t(currentDate.toLocaleString('en-IN', { month: 'long' }))} {year}</span>
            <span className="lp-vcal-hindu" style={{ color: getMeta(activeEvent).color }}>
              {getMeta(activeEvent).icon} {t(getMeta(activeEvent).english)}
            </span>
          </div>
          <button className="lp-vcal-nav" onClick={handleNext}>›</button>
        </div>

        {/* ── Muhurat Cards ── */}
        {loading ? (
          <div className="lp-vcal-loading" style={{ textAlign: "center", padding: "4rem", color: "var(--text-dim)", fontSize: "0.9rem", letterSpacing: "0.5px" }}>{t('Computing muhurats…')}</div>
        ) : (
          <div className="lp-muh-list" ref={listRef}>
            {filteredDays.map(day => {
              const m = day.eventMuhurat;
              if (!m) return null;
              const meta = getMeta(activeEvent);
              const isToday = day.date === todayStr;
              const isExpand = expanded === day.day;
              const qBg = QUALITY_BG[m.quality] || 'rgba(255,255,255,0.03)';

              return (
                <div
                  key={day.day}
                  className={`lp-muh-card${isToday ? ' today' : ''}${isExpand ? ' expanded' : ''}`}
                  style={{ background: qBg, borderLeftColor: m.qualityColor }}
                  onClick={() => setExpanded(isExpand ? null : day.day)}
                >
                  {/* ── Card Header ── */}
                  <div className="lp-muh-card-header">
                    <div className="lp-muh-date-col">
                      <div className="lp-muh-daynum" style={isToday ? { background: 'var(--gold)', color: '#0d0d1a', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}>
                        {day.day}
                      </div>
                      <div className="lp-muh-dayinfo">
                        <div className="lp-muh-weekday">{t(WEEKDAY_SHORT[day.weekday])}</div>
                        <div className="lp-muh-month">{t(currentDate.toLocaleString('en-IN', { month: 'short' }))}</div>
                      </div>
                    </div>

                    <div className="lp-muh-panchang-row">
                      <span className="lp-muh-pitem"><span className="lp-muh-plbl">{t('Tithi')}</span> {t(day.paksha[0])}{day.tithiNum} {t(day.tithi)}</span>
                      <span className="lp-muh-pitem"><span className="lp-muh-plbl">{t('Nakshatra')}</span> {t(day.nakshatra)}</span>
                      <span className="lp-muh-pitem"><span className="lp-muh-plbl">{t('Yoga')}</span> {t(day.yoga)}</span>
                    </div>

                    <div className="lp-muh-quality-col">
                      <span className="lp-muh-badge" style={{ background: m.qualityColor + '22', color: m.qualityColor, border: `1px solid ${m.qualityColor}55` }}>
                        {m.qualityEmoji} {t(m.quality)}
                      </span>
                      <div className="lp-muh-score-bar">
                        <div style={{ width: `${m.score}%`, background: m.qualityColor, height: '100%', borderRadius: 4, transition: 'width 0.5s' }} />
                      </div>
                    </div>

                    {m.window && (
                      <div className="lp-muh-window-col">
                        <div className="lp-muh-window-lbl">{t('Best Time')}</div>
                        <div className="lp-muh-window-val">
                          {m.window.start} <span style={{ opacity: 0.5 }}>–</span> {m.window.end}
                        </div>
                        <div className="lp-muh-rahu">⚠ {t('Rahu')}: {day.rahuKaal.start}–{day.rahuKaal.end}</div>
                      </div>
                    )}

                    <div className="lp-muh-chevron">{isExpand ? '▲' : '▼'}</div>
                  </div>

                  {/* ── Expanded Detail ── */}
                  {isExpand && (
                    <div className="lp-muh-detail">
                      <div className="lp-muh-detail-cols">
                        {/* Auspicious reasons */}
                        {m.reasons.length > 0 && (
                          <div className="lp-muh-detail-block">
                            <div className="lp-muh-detail-title" style={{ color: '#2ECC71' }}>✅ {t('Auspicious Factors')}</div>
                            {m.reasons.map((r, i) => <div key={i} className="lp-muh-detail-item">{translateReason(r)}</div>)}
                          </div>
                        )}
                        {/* Avoid reasons */}
                        {m.avoidReasons.length > 0 && (
                          <div className="lp-muh-detail-block">
                            <div className="lp-muh-detail-title" style={{ color: '#E74C3C' }}>⚠️ {t('Caution')}</div>
                            {m.avoidReasons.map((r, i) => <div key={i} className="lp-muh-detail-item warn">{translateReason(r)}</div>)}
                          </div>
                        )}
                        {/* Timing */}
                        <div className="lp-muh-detail-block">
                          <div className="lp-muh-detail-title">⏰ {t('Timings')}</div>
                          <div className="lp-muh-detail-item">🌅 {t('Sunrise')}: {day.sunriseIST} IST</div>
                          <div className="lp-muh-detail-item">🌇 {t('Sunset')}: {day.sunsetIST} IST</div>
                          <div className="lp-muh-detail-item" style={{ color: '#E74C3C' }}>
                            🚫 {t('Rahu Kaal')}: {day.rahuKaal.start}–{day.rahuKaal.end} IST
                          </div>
                          {day.abhijit && (
                            <div className="lp-muh-detail-item" style={{ color: '#2ECC71' }}>
                              ✨ {t('Abhijit')}: {day.abhijit.start}–{day.abhijit.end} IST
                            </div>
                          )}
                          {m.window && (
                            <div className="lp-muh-detail-item" style={{ color: meta.color, fontWeight: 700 }}>
                              🕉 Best window: {m.window.start}–{m.window.end} IST
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {filteredDays.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-dim)' }}>
                {t('No muhurat data available for this month.')}
              </div>
            )}
          </div>
        )}
      </div>{/* /lp-muh-wrap */}
    </section>
  );
};


/* ── Pricing Section ─────────────────────────────────────── */
const FREE_FEATURES = [
  'Live planetary positions & detailed data',
  'Daily Panchang — Tithi, Nakshatra, Yoga, Karana',
  'Vedic Hindu Calendar with festivals & events',
  'Vedic Muhurat — auspicious timings for all events',
  'Daily horoscope for all 12 Rashis',
];

const PRO_FEATURES = [
  { text: 'All Free features included', divider: true },
  'Personal birth chart & Kundali',
  'Transit comparison with your natal chart',
  'Outer planets & Upagrahas — positions & transit analysis',
  'North Indian & South Indian chart styles',
  'Planet & House strength analysis (Shadbala, Bhava Bala)',
  'Dasha analysis — Vimshottari & sub-dashas',
  'Bhrigu Nandi Nadi Analysis',
  'Nakshatra Analysis — Pada, Deity, Qualities',
  'Numerology Analysis',
  { text: 'Predictions', isNew: true },
  'Personalised remedies & suggestions',
  'Create, save & manage multiple charts',
];

const PricingSection = ({ onSignUp }) => {
  const { t } = useLanguage();
  const { currentUser, isPremium } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [promoCode, setPromoCode] = useState("");

  const handleProAction = async () => {
    if (!currentUser) {
      onSignUp();
      return;
    }
    if (isPremium) {
      navigate('/dashboard');
      return;
    }

    setLoading(true);
    setError("");

    const res = await loadRazorpayScript();
    if (!res) {
      setError('Razorpay SDK failed to load. Are you online?');
      setLoading(false);
      return;
    }

    const subData = await createRazorpaySubscription(currentUser.uid, promoCode);

    if (!subData.success) {
      setError(subData.message || 'Failed to initialize payment.');
      setLoading(false);
      return;
    }

    const { id: subscription_id } = subData.subscription;

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_T16QE5DLsNltdy',
      subscription_id: subscription_id,
      name: 'Cospyria',
      description: 'Cospyria Pro Monthly Subscription',
      image: window.location.origin + '/cospyria-logo.png',
      handler: async function (response) {
        // Optimistically upgrade the user immediately so they don't have to wait for the webhook
        try {
          await setDoc(doc(db, "users", currentUser.uid), {
            isPremium: true,
            premiumSince: serverTimestamp(),
            lastSubscriptionId: subscription_id
          }, { merge: true });
        } catch (e) {
          console.error("Instant upgrade failed:", e);
        }
        
        alert('Payment successful! Welcome to Cospyria Pro. Your profile has been upgraded.');
        navigate('/dashboard');
      },
      prefill: {
        name: currentUser.displayName || '',
        email: currentUser.email || '',
        contact: ''
      },
      theme: {
        color: '#D4AF37'
      }
    };

    const paymentObject = new window.Razorpay(options);
    paymentObject.on('payment.failed', function (response) {
      setError(response.error.description || 'Payment Failed');
    });

    paymentObject.open();
    setLoading(false);
  };

  return (
    <section className="lp-section" id="pricing">
      <div className="lp-section-header">
        <span className="lp-section-badge">✨ {t('Flexible Plans')}</span>
        <h2 className="lp-section-title">{t('Simple, Transparent Pricing')}</h2>
        <p className="lp-section-sub">{t('Begin your Vedic journey for free — unlock the full cosmos with Pro')}</p>
      </div>

      <div className="lp-price-grid">

        {/* ── Free Plan ── */}
        <div className="lp-price-card lp-price-free">
          <div className="lp-price-card-top">
            <div className="lp-price-badge-pill" style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--gold)', border: '1px solid rgba(212,175,55,0.3)' }}>{t('Free Forever')}</div>
            <div className="lp-price-plan-name">{t('Free')}</div>
            <div className="lp-price-amount">
              <span className="lp-price-currency">₹</span>
              <span className="lp-price-num">0</span>
            </div>
            <div className="lp-price-period">{t('No credit card required')}</div>
          </div>
          <div className="lp-price-divider" />
          <ul className="lp-price-features">
            {FREE_FEATURES.map((text, i) => (
              <li key={i} className="lp-price-feature-item">
                <svg className="lp-check" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7.5" stroke="rgba(212,175,55,0.35)" /><path d="M5 8.5l2 2 4-4" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                <span>{t(text)}</span>
              </li>
            ))}
          </ul>
          <div className="lp-price-card-footer">
            <div className="lp-price-free-note">{t('Always free · No account required')}</div>
          </div>
        </div>

        {/* ── Pro Plan ── */}
        <div className="lp-price-card lp-price-pro">
          <div className="lp-price-popular-badge">{t('Most Popular')}</div>
          <div className="lp-price-card-top">
            <div className="lp-price-badge-pill" style={{ background: 'rgba(212,175,55,0.12)', color: 'var(--gold)', border: '1px solid rgba(212,175,55,0.3)' }}>{t('Full Access')}</div>
            <div className="lp-price-plan-name">{t('Pro')}</div>
            <div className="lp-price-amount">
              <span className="lp-price-currency">₹</span>
              <span className="lp-price-num">499</span>
              <span className="lp-price-per">{t('/mo')}</span>
            </div>
            <div className="lp-price-period">{t('Billed monthly · Cancel anytime')}</div>
          </div>
          <div className="lp-price-divider" />
          <ul className="lp-price-features">
            {PRO_FEATURES.map((f, i) => {
              const isDivider = typeof f === 'object' && f.divider;
              const isNew = typeof f === 'object' && f.isNew;
              const text = typeof f === 'object' ? f.text : f;
              return (
                <li key={i} className={`lp-price-feature-item${isDivider ? ' pro-divider-item' : ''}`}>
                  <svg className="lp-check" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7.5" stroke="rgba(212,175,55,0.4)" /><path d="M5 8.5l2 2 4-4" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <span style={isDivider ? { color: 'var(--gold)', fontWeight: 600 } : {}}>
                    {t(text)}
                    {isNew && <span style={{ marginLeft: '8px', background: '#e74c3c', color: '#fff', fontSize: '0.65rem', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', textTransform: 'uppercase', verticalAlign: 'middle' }}>New</span>}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="lp-price-card-footer" style={{ textAlign: 'center' }}>
            {!isPremium && (
              <div style={{ marginBottom: '1.2rem', width: '100%', display: 'flex', justifyContent: 'center' }}>
                <input 
                  type="text" 
                  placeholder={t("Have a promo code?")}
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  disabled={loading}
                  style={{
                    width: '100%', maxWidth: '240px', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(212,175,55,0.3)', borderRadius: '8px',
                    color: 'var(--gold)', fontFamily: 'Outfit', letterSpacing: '2px', textAlign: 'center',
                    outline: 'none', transition: 'all 0.3s', fontSize: '0.9rem'
                  }}
                />
              </div>
            )}
            {error && <div style={{ color: '#ff6b6b', fontSize: '0.85rem', marginBottom: '10px' }}>{error}</div>}
            <button className="lp-price-pro-btn" onClick={handleProAction} disabled={loading}>
              {loading ? t('Processing...') : isPremium ? t('Go to Dashboard') : t('Get Pro Access')}
            </button>
            <div className="lp-price-guarantee">{t('Secure payment · Cancel anytime')}</div>
          </div>
        </div>

      </div>
    </section>
  );
};

/* ── Donation Section ──────────────────────────────────────── */
const DonationSection = () => {
  const { t } = useLanguage();
  const { currentUser } = useAuth();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDonate = async () => {
    if (!amount || isNaN(amount) || amount < 1) {
      setError('Please enter a valid amount (minimum ₹1).');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const res = await loadRazorpayScript();
      if (!res) {
        setError('Razorpay SDK failed to load.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API}/api/payments/create-donation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parseInt(amount) * 100, userId: currentUser?.uid || 'guest' })
      });
      const data = await response.json();
      
      if (!data.success) {
        setError('Failed to initialize donation.');
        setLoading(false);
        return;
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_T16QE5DLsNltdy',
        amount: data.order.amount,
        currency: "INR",
        name: 'Cospyria',
        description: 'Support Our Vedic Engine',
        image: window.location.origin + '/cospyria-logo.png',
        order_id: data.order.id,
        handler: function (response) {
          alert('Thank you so much for your support! Your contribution helps us improve the Vedic engine.');
          setAmount('');
        },
        prefill: {
          name: currentUser?.displayName || '',
          email: currentUser?.email || ''
        },
        theme: {
          color: '#D4AF37'
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (e) {
      setError('An error occurred.');
      console.error(e);
    }
    setLoading(false);
  };

  return (
    <section className="lp-section" id="donate" style={{ marginTop: '4rem', paddingBottom: '4rem' }}>
      <div className="lp-section-header">
        <span className="lp-section-badge">🙏 {t('Support Us')}</span>
        <h2 className="lp-section-title">{t('Help Us Improve Our Vedic Engine')}</h2>
        <p className="lp-section-sub" style={{ maxWidth: '600px', margin: '0 auto' }}>{t('Cospyria is built with love and ancient wisdom. Your contributions allow us to refine our astrological algorithms and bring more features to the community.')}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center', width: '100%', maxWidth: '400px', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ position: 'relative', width: '250px' }}>
            <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--gold)', fontWeight: 'bold' }}>₹</span>
            <input 
              type="number" 
              value={amount} 
              onChange={e => setAmount(e.target.value)} 
              placeholder="Custom Amount"
              style={{ padding: '1rem 1rem 1rem 2.5rem', borderRadius: '8px', border: '1px solid rgba(212,175,55,0.3)', background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '1rem', width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <button className="lp-price-pro-btn" onClick={handleDonate} disabled={loading} style={{ width: '250px', padding: '1rem' }}>
            {loading ? t('Processing...') : t('Donate')}
          </button>
        </div>
        {error && <div style={{ color: '#ff6b6b' }}>{error}</div>}
      </div>
    </section>
  );
};



/* ── Footer ──────────────────────────────────────────────── */
export const Footer = ({ onSignUp }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleScroll = (id) => {
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="lp-footer">
      <div className="lp-footer-inner">

        {/* ── Brand column ── */}
        <div className="lp-footer-col lp-footer-brand-col">
          <Link to="/" className="lp-footer-brand" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer', marginBottom: '1rem' }}>
            <CosmicLogo size={48} style={{ marginRight: '14px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '4px' }}>
              <span className="text-gradient-gold" style={{ fontFamily: 'Antonio, sans-serif', fontSize: '1.8rem', fontWeight: '600', letterSpacing: '6px', margin: 0, lineHeight: '1', textTransform: 'uppercase' }}>COSPYRIA</span>
              <span style={{ fontSize: '0.6rem', color: '#fff', letterSpacing: '2px', fontWeight: '300', marginTop: '4px' }}>VEDIC ASTROLOGY ENGINE</span>
            </div>
          </Link>
          <p className="lp-footer-tagline">
            {t('Ancient Vedic wisdom, modernised.')}<br />
            {t('All calculations powered by Swiss Ephemeris.')}
          </p>
          <div className="lp-footer-badge">
            <span>🕉</span> {t('Vedic Astrology Engine')}
          </div>
          <div className="lp-social-links" style={{ display: 'flex', gap: '15px', marginTop: '1.5rem' }}>
            <a href="https://www.producthunt.com/products/cospyria?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-cospyria" target="_blank" rel="noreferrer" className="lp-social-icon" title="Product Hunt">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M10 8h3.5a2.5 2.5 0 0 1 0 5H10v3"></path></svg>
            </a>
            <a href="https://www.facebook.com/cospyria" target="_blank" rel="noreferrer" className="lp-social-icon" title="Facebook">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
            </a>
            <a href="https://www.instagram.com/cospyria/" target="_blank" rel="noreferrer" className="lp-social-icon" title="Instagram">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
            </a>
            <a href="https://whatsapp.com/channel/0029VbCjx304tRrsdBNhn32W" target="_blank" rel="noreferrer" className="lp-social-icon" title="WhatsApp Channel">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
            </a>
          </div>
        </div>

        {/* ── Explore column ── */}
        <div className="lp-footer-col">
          <div className="lp-footer-col-title">{t('Explore')}</div>
          <ul className="lp-footer-links">
            <li><button onClick={() => handleScroll('panchang')}>{t("Today's Panchang")}</button></li>
            <li><button onClick={() => handleScroll('horoscope')}>{t('Vedic Horoscope')}</button></li>
            <li><button onClick={() => handleScroll('calendar')}>{t('Vedic Calendar')}</button></li>
            <li><button onClick={() => handleScroll('muhurat')}>{t('Vedic Muhurat')}</button></li>
            <li><button onClick={() => handleScroll('pricing')}>{t('Pricing')}</button></li>
          </ul>
        </div>

        {/* ── Features column ── */}
        {/* ── Resources column ── */}
        <div className="lp-footer-col">
          <div className="lp-footer-col-title">{t('Resources')}</div>
          <ul className="lp-footer-links">
            <li><Link to="/docs">{t('Documentation')}</Link></li>
            <li><Link to="/guide">{t('Vedic Astrology Guide')}</Link></li>
            <li><Link to="/bnn-guide">{t('Bhrigu Nandi Nadi Guide')}</Link></li>
            <li><Link to="/nakshatra-guide">{t('Nakshatra Guide')}</Link></li>
            <li><Link to="/numerology-guide">{t('Numerology Guide')}</Link></li>

          </ul>
        </div>


        {/* ── Legal column ── */}
        <div className="lp-footer-col">
          <div className="lp-footer-col-title">{t('Legal')}</div>
          <ul className="lp-footer-links">
            <li><Link to="/privacy-policy">{t('Privacy Policy')}</Link></li>
            <li><Link to="/terms-of-service">{t('Terms of Service')}</Link></li>
            <li><Link to="/refund-policy">{t('Refund Policy')}</Link></li>
            <li><Link to="/disclaimer">{t('Disclaimer')}</Link></li>
            <li><Link to="/contact">{t('Contact Us')}</Link></li>
          </ul>
        </div>

      </div>

      {/* ── Bottom bar ── */}
      <div className="lp-footer-bottom">
        <span>© {new Date().getFullYear()} Cospyria · {t('All rights reserved')}</span>
        <div className="lp-footer-legal-links">
          {t('Built with Swiss Ephemeris · Lahiri Ayanamsa · IST timezone')}
        </div>
      </div>
    </footer>
  );
};

const UpsellModal = ({ onClose }) => {
  const { t } = useLanguage();
  return (
    <div className="lp-modal-overlay" style={{ zIndex: 9999 }}>
      <div className="lp-modal" style={{ maxWidth: '400px', textAlign: 'center' }}>
        <button className="lp-modal-close" onClick={onClose}>&times;</button>
        <div className="lp-modal-header">
          <span className="lp-modal-icon">🎉</span>
          <h2 className="text-gradient-gold">{t('Congratulations!')}</h2>
          <p style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
            {t('Buy our Pro plan to unlock your personalized cosmic dashboard!')}
          </p>
        </div>
        <button className="kf-submit" onClick={() => { onClose(); document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' }); }}>
          <span>✦</span> {t('Get Pro Access')}
        </button>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const { currentUser } = useAuth();
  const [modalMode, setModalMode] = useState(null); // "signin" | "signup"
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  const location = useLocation();

  // Always enforce dark mode — light mode removed
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
    localStorage.removeItem('theme');
  }, []);

  useEffect(() => {
    if (location.state?.showUpsell) {
      setShowUpsellModal(true);
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Auto-redirect removed to allow signed-in users to view the landing page

  const handleAuthSuccess = (isPrem) => {
    setModalMode(null);
    if (isPrem) {
      navigate("/dashboard");
    } else {
      setShowUpsellModal(true);
    }
  };

  const handleSignIn = () => {
    setModalMode("signin");
  };

  const handleGetStarted = () => {
    if (currentUser) {
      navigate("/dashboard");
    } else {
      setModalMode("signup");
    }
  };

  return (
    <div className="lp-root">
      <LandingNavbar
        onSignIn={handleSignIn}
        onSignUp={() => setModalMode("signup")}
        currentUser={currentUser}
        onProfileClick={() => setShowProfileModal(true)}
      />

      {showProfileModal && (
        <UserProfileModal onClose={() => setShowProfileModal(false)} />
      )}

      {showUpsellModal && (
        <UpsellModal onClose={() => setShowUpsellModal(false)} />
      )}

      {modalMode && (
        <AuthModal
          mode={modalMode}
          onClose={() => setModalMode(null)}
          onAuthSuccess={handleAuthSuccess}
          onToggleMode={() => setModalMode(prev => prev === "signin" ? "signup" : "signin")}
        />
      )}

      <HeroSection onGetStarted={handleGetStarted} />
      <QuoteSection />
      <PanchangSection />
      <HoroscopeSection onGetStarted={handleGetStarted} />
      <VedicCalendarSection />
      <VedicMuhuratSection />
      
      {/* 8. Testimonials Section */}


      {/* 9. Pricing Section */}
      <PricingSection />
      
      {/* 10. Donation Section */}
      <DonationSection />

      <Footer onSignUp={() => setModalMode("signup")} />
    </div>
  );
};

export default LandingPage;
